import React, { createContext, useState, useEffect, useContext } from "react";
import api from "../api/axios";
import { ADMINS } from "../constants/roles";
import { useCurrentUser } from "../utilities/currentUser";
import { userHasAnyRole } from "../utils/leadershipRoles";

const SettingsContext = createContext();

export const useSettings = () => useContext(SettingsContext);

const defaultSettings = {
  companyName: "Iconic Greenland",
  primaryColor: "#4f46e5",
  secondaryColor: "#64748b",
  logo: null,
  defaultBookingFee: 10000,
  defaultDownpayment: 10,
  email: "admin@iconicgreenland.com",
  phone: "+880 1711-000000",
  currency: "BDT",
  timezone: "UTC+6 (Dhaka)",
  gateway: "Bkash",
  merchantId: "",
  address: "3 RK Mission Rd, Dhaka, Bangladesh",
};

export const SettingsProvider = ({ children }) => {
  const [globalSettings, setGlobalSettings] = useState(() => {
    const saved = localStorage.getItem("appSettings");
    return saved ? { ...defaultSettings, ...JSON.parse(saved) } : defaultSettings;
  });
  const [loadingSettings, setLoadingSettings] = useState(false);
  // GET /api/settings/app is an Admin / Superadmin endpoint (403 for everyone else), so only admins ask for it.
  // Other users keep the defaults, plus whatever an admin session cached in this browser.
  const { user } = useCurrentUser();
  const isAdmin = userHasAnyRole(user, ADMINS);

  useEffect(() => {
    document.documentElement.style.setProperty(
      "--primary",
      globalSettings.primaryColor
    );
  }, [globalSettings.primaryColor]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token || !isAdmin) return;

    let cancelled = false;
    const load = async () => {
      setLoadingSettings(true);
      try {
        const res = await api.get("/api/settings/app");
        if (!cancelled && res.data?.data) {
          const merged = { ...defaultSettings, ...res.data.data };
          setGlobalSettings(merged);
          localStorage.setItem("appSettings", JSON.stringify(merged));
        }
      } catch (err) {
        // Reason: keep localStorage defaults if the API fails
        console.warn("Could not load system settings from API", err?.message);
      } finally {
        if (!cancelled) setLoadingSettings(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [isAdmin]);

  const updateSettings = async (newSettings) => {
    const updated = { ...globalSettings, ...newSettings };
    setGlobalSettings(updated);
    localStorage.setItem("appSettings", JSON.stringify(updated));

    try {
      const res = await api.put("/api/settings/app", updated);
      if (res.data?.data) {
        const fromServer = { ...defaultSettings, ...res.data.data };
        setGlobalSettings(fromServer);
        localStorage.setItem("appSettings", JSON.stringify(fromServer));
      }
    } catch (err) {
      console.error("Failed to persist settings", err);
      throw err;
    }
  };

  return (
    <SettingsContext.Provider
      value={{ globalSettings, updateSettings, loadingSettings }}
    >
      {children}
    </SettingsContext.Provider>
  );
};
